import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Link,
  Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import HomeIcon from "@mui/icons-material/Home";
import PlaceIcon from "@mui/icons-material/Place";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MapIcon from "@mui/icons-material/Map";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoIcon from "@mui/icons-material/Info";
import PeopleIcon from "@mui/icons-material/People";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNavigate, useLocation } from "react-router-dom";
import { useThemeStore } from "../../services/themeStore";
import { useAuth } from "../../contexts/AuthContext";
import { useNotificationsStore } from "../../services/notificationsStore";
import { NotificationsPanel } from "../common/NotificationsPanel";
import { 
  approveShareRequest, 
  rejectShareRequest, 
  approveFriendRequest, 
  rejectFriendRequest 
} from "../../services/requestsService";
import { 
  deleteNotificationFromFirestore
} from "../../services/notificationsSyncService";
import type { Notification, ShareRequestNotification, FriendRequestNotification } from "../../types";
import { ROUTES } from "../../constants";

const drawerWidth = 240;

const navItems = [
  { label: "Home", path: ROUTES.HOME, icon: <HomeIcon /> },
  { label: "Places", path: ROUTES.PLACES, icon: <PlaceIcon /> },
  { label: "Experiences", path: ROUTES.EXPERIENCES, icon: <EmojiEventsIcon /> },
  { label: "Map View", path: ROUTES.MAP_VIEW, icon: <MapIcon /> },
];

/**
 * Main navigation bar component
 */
export const Navbar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleTheme } = useThemeStore();
  const { user, logout } = useAuth();
  const { notifications, getUnreadCount, removeNotification } =
    useNotificationsStore();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate("/login");
    } catch (error) {
      // Silent error handling
    }
  };

  const handleNotificationsOpen = () => {
    setNotificationsOpen(true);
  };

  const handleNotificationsClose = () => {
    setNotificationsOpen(false);
  };

  const handleApproveNotification = async (notification: Notification) => {
    try {
      // Handle approval based on type
      if (notification.type === 'share-request') {
        await approveShareRequest(notification as ShareRequestNotification, user?.uid || '');
      } else if (notification.type === 'friend-request') {
        await approveFriendRequest(notification as FriendRequestNotification, user?.uid || '');
      }
      
      // Delete from Firestore after successful approval
      if (user) {
        try {
          await deleteNotificationFromFirestore(user.uid, notification.id);
        } catch (error) {
          console.error('Error deleting notification from Firestore:', error);
        }
      }
    } catch (error) {
      console.error('Error approving notification:', error);
    }
  };

  const handleRejectNotification = async (notification: Notification) => {
    try {
      // Handle rejection based on type
      if (notification.type === 'share-request') {
        await rejectShareRequest(notification as ShareRequestNotification);
      } else if (notification.type === 'friend-request') {
        await rejectFriendRequest(notification as FriendRequestNotification);
      }
      
      // Delete from Firestore after successful rejection
      if (user) {
        try {
          await deleteNotificationFromFirestore(user.uid, notification.id);
        } catch (error) {
          console.error('Error deleting notification from Firestore:', error);
        }
      }
    } catch (error) {
      console.error('Error rejecting notification:', error);
    }
  };

  const handleDismissNotification = (notificationId: string) => {
    removeNotification(notificationId);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center", display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between" }}>
      <Box>
        <Typography variant="h6" sx={{ my: 2 }}>
          GoDo
        </Typography>
        <List>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      <Typography
        variant="caption"
        sx={{ pb: 2, px: 2, color: theme.palette.text.secondary }}
      >
        © 2026{" "}
        <Link
          href="https://getgodo.net"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: theme.palette.text.primary,
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          GoDo
        </Link>
      </Typography>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" elevation={1}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, cursor: "pointer" }}
            onClick={() => navigate(ROUTES.HOME)}
          >
            GoDo
          </Typography>
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {navItems.map((item) => (
                <IconButton
                  key={item.path}
                  color="inherit"
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 1,
                    px: 2,
                    backgroundColor:
                      location.pathname === item.path
                        ? "rgba(255, 255, 255, 0.1)"
                        : "transparent",
                  }}
                >
                  {item.icon}
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {item.label}
                  </Typography>
                </IconButton>
              ))}
            </Box>
          )}
          <IconButton onClick={toggleTheme} color="inherit" sx={{ ml: 1 }}>
            {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          {user && (
            <>
              <IconButton
                onClick={handleNotificationsOpen}
                color="inherit"
                sx={{ ml: 1 }}
              >
                <Badge badgeContent={getUnreadCount()} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <IconButton onClick={handleMenuOpen} sx={{ ml: 1 }}>
                <Avatar
                  src={user.photoURL || undefined}
                  alt={user.displayName || "User"}
                  sx={{ width: 32, height: 32 }}
                >
                  {!user.photoURL && <PersonIcon />}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem disabled>
                  <ListItemText
                    primary={user.displayName || "User"}
                    secondary={user.email}
                    primaryTypographyProps={{ fontWeight: "bold" }}
                  />
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => navigate(ROUTES.FRIENDS)}>
                  <ListItemIcon>
                    <PeopleIcon fontSize="small" />
                  </ListItemIcon>
                  Friends
                </MenuItem>
                <MenuItem onClick={() => navigate(ROUTES.ABOUT)}>
                  <ListItemIcon>
                    <InfoIcon fontSize="small" />
                  </ListItemIcon>
                  About
                </MenuItem>
                <MenuItem onClick={() => navigate(ROUTES.SETTINGS)}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      {isMobile && mobileOpen && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      )}
      <NotificationsPanel
        open={notificationsOpen}
        onClose={handleNotificationsClose}
        notifications={notifications}
        onApprove={handleApproveNotification}
        onReject={handleRejectNotification}
        onDismiss={handleDismissNotification}
      />
    </>
  );
};
